import { memberDisplayName, ORG_ROLES, type OrgMember, type OrgRole } from '@noconflict/contracts';
import { useTranslation } from 'react-i18next';
import { useSetOrgRole } from '../../../entities/member';
import { identityErrorMessage } from '../../../shared/api/identity-error';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../shared/ui/select';
import { toast } from '../../../shared/ui/sonner';

/**
 * Org role of one member (`members.manage`). Only an owner may grant or take away `owner`, so for anyone
 * else the owner option is absent and an owner's row is read-only. The server enforces the same rules and
 * the last-owner rule; its refusal goes to `onError`.
 */
export function MemberRoleSelect({
  org,
  member,
  callerIsOwner,
  onError,
}: {
  org: { id: string };
  member: OrgMember;
  callerIsOwner: boolean;
  onError: (message: string | null) => void;
}) {
  const { t } = useTranslation('member');
  const setRole = useSetOrgRole(org.id);
  const roles = ORG_ROLES.filter((role) => callerIsOwner || role !== 'owner');
  const locked = member.role === 'owner' && !callerIsOwner;
  return (
    <Select
      value={member.role}
      disabled={locked || setRole.isPending}
      onValueChange={async (role) => {
        onError(null);
        try {
          await setRole.mutateAsync({ userId: member.user_id, role: role as OrgRole });
          toast.success(t('roleChanged'));
        } catch (e) {
          onError(identityErrorMessage(e));
        }
      }}
    >
      <SelectTrigger
        size="compact"
        className="w-36"
        aria-label={t('roleFor', { name: memberDisplayName(member) })}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {(locked ? ORG_ROLES : roles).map((role) => (
          <SelectItem key={role} value={role}>
            {t(`roles.${role}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
